import { Controller } from "@hotwired/stimulus"
import { Calendar } from "vanilla-calendar-pro"

const TRIGGER_EVENTS = ["focus", "click", "blur"]

export default class extends Controller {
  static targets = ["trigger", "startInput", "endInput", "validation"]
  static values = { locale: String, applyLabel: String, cancelLabel: String }

  connect() {
    this.onFooterClick = (event) => {
      const button = event.target.closest("[data-vc-btn]")
      if (!button || !this.calendar.context.mainElement?.contains(button)) return

      button.dataset.vcBtn === "apply" ? this.apply() : this.calendar.hide()
    }
    document.addEventListener("click", this.onFooterClick)

    this.deferOpen = (event) => {
      clearTimeout(this.openTimer)
      if (event.type === "blur" || this.opening) return

      event.stopImmediatePropagation()
      this.openTimer = setTimeout(() => {
        this.opening = true
        this.triggerTarget.click()
        this.opening = false
      }, 100)
    }
    TRIGGER_EVENTS.forEach((type) => this.triggerTarget.addEventListener(type, this.deferOpen))

    this.calendar = new Calendar(this.triggerTarget, {
      inputMode: true,
      positionToInput: "auto",
      locale: this.localeValue,
      selectedTheme: "light",
      type: "multiple",
      displayMonthsCount: 2,
      displayDatesOutside: false,
      firstWeekday: 0,
      selectedWeekends: [],
      selectionDatesMode: "multiple-ranged",
      enableEdgeDatesOnly: true,
      selectedDates: this.committedDates(),
      layouts: { multiple: this.multipleLayout() },
      onShow: (self) => {
        self.set({ selectedDates: this.committedDates() })
        self.context.mainElement.setAttribute("data-a11y-dialog-ignore-focus-trap", "")
        this.syncApplyButton(self)
      },
      onHide: () => clearTimeout(this.openTimer),
      onClickDate: (self) => this.syncApplyButton(self),
      onUpdate: (self) => this.syncApplyButton(self)
    })
    this.calendar.init()
    this.syncTrigger()
  }

  disconnect() {
    document.removeEventListener("click", this.onFooterClick)
    TRIGGER_EVENTS.forEach((type) => this.triggerTarget.removeEventListener(type, this.deferOpen))
    clearTimeout(this.openTimer)
    this.calendar.hide()
    this.calendar.destroy()
  }

  apply() {
    const dates = this.draftDates()
    if (!dates.length) return

    this.startInputTarget.value = dates[0]
    this.endInputTarget.value = dates[dates.length - 1]
    if (this.hasValidationTarget) this.validationTarget.value = "1"
    this.syncTrigger()
    this.calendar.hide()
  }

  draftDates() {
    return [...(this.calendar.context.selectedDates || [])].filter(Boolean).sort()
  }

  syncTrigger() {
    const dates = this.committedDates()
    if (!dates.length) return

    this.triggerTarget.value = `${this.format(dates[0])} - ${this.format(dates[1])}`
    this.triggerTarget.classList.add("filled")
  }

  syncApplyButton(self) {
    requestAnimationFrame(() => {
      const button = self.context.mainElement.querySelector('[data-vc-btn="apply"]')
      if (button) button.disabled = !this.draftDates().length
    })
  }

  committedDates() {
    const start = this.startInputTarget.value
    const end = this.endInputTarget.value
    return start && end ? [start, end] : []
  }

  format(iso) {
    const [year, month, day] = iso.split("-").map(Number)
    return new Intl.DateTimeFormat(this.localeValue || "en", { month: "short", day: "numeric", year: "numeric" })
      .format(new Date(year, month - 1, day))
  }

  multipleLayout() {
    return `
      <div class="vc-controls" data-vc="controls" role="toolbar">
        <#ArrowPrev [month] />
        <#ArrowNext [month] />
      </div>
      <div class="vc-grid" data-vc="grid">
        <#Multiple>
          <div class="vc-column" data-vc="column" role="region">
            <div class="vc-header" data-vc="header">
              <div class="vc-header__content" data-vc-header="content">
                <#Month />
                <#Year />
              </div>
            </div>
            <div class="vc-wrapper" data-vc="wrapper">
              <div class="vc-content" data-vc="content" role="grid">
                <#Week />
                <#Dates />
              </div>
            </div>
          </div>
        <#/Multiple>
        <#DateRangeTooltip />
      </div>
      <div class="drp-footer">
        <button type="button" class="md-btn cancel-btn" data-vc-btn="cancel">${this.cancelLabelValue}</button>
        <button type="button" class="md-btn md-primary-btn md-blue-btn" data-vc-btn="apply" disabled>${this.applyLabelValue}</button>
      </div>
    `
  }
}
